import type React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { debounce } from 'ui';

import type { MobileZoom } from '../../utils/canvas/canvas';
import { useTouchPan } from './useTouchPan';
import { useTouchZoom } from './useTouchZoom';

interface UseTouchHandlersProps {
  isMobile: boolean;
  canvasRef: React.RefObject<HTMLCanvasElement>;
}

interface UseTouchHandlersReturn {
  mobileZoom: MobileZoom;
  setMobileZoom: React.Dispatch<React.SetStateAction<MobileZoom>>;
}

export const useTouchHandlers = ({ isMobile, canvasRef }: UseTouchHandlersProps): UseTouchHandlersReturn => {
  const touchZoom = useTouchZoom({ canvasRef, isMobile });
  const touchPan = useTouchPan({
    canvasRef,
    isMobile,
    mobileZoom: touchZoom.mobileZoom,
    setMobileZoom: touchZoom.setMobileZoom,
  });

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      if (!isMobile) return;

      if (event.touches.length === 2) {
        // Two-finger pinch to zoom
        touchZoom.handleTouchStart(event);
        touchPan.setIsPanning(false);
      } else if (event.touches.length === 1 && touchZoom.mobileZoom.scale > 1) {
        // Single finger pan when zoomed in
        touchPan.handleTouchStart(event);
      }
    },
    [isMobile, touchZoom, touchPan]
  );

  const handleTouchMoveInternal = useCallback(
    (event: TouchEvent) => {
      if (!isMobile) return;

      if (event.touches.length === 2 && touchZoom.mobileZoom.isZooming) {
        // Two-finger pinch to zoom
        touchZoom.handleTouchMove(event);
      } else if (event.touches.length === 1 && touchPan.isPanning && touchZoom.mobileZoom.scale > 1) {
        // Single finger pan when zoomed in
        touchPan.handleTouchMove(event);
      }
    },
    [isMobile, touchZoom, touchPan]
  );

  const handleTouchMove = useMemo(() => debounce(handleTouchMoveInternal, 16), [handleTouchMoveInternal]);

  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      if (!isMobile) return;

      touchZoom.handleTouchEnd(event);
      touchPan.handleTouchEnd(event);
    },
    [isMobile, touchZoom, touchPan]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !isMobile) return;

    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, isMobile, canvasRef]);

  return {
    mobileZoom: touchZoom.mobileZoom,
    setMobileZoom: touchZoom.setMobileZoom,
  };
};
